import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  AmhsRunReports,
  BrowserKinetic,
  SimulationAssignment,
  TransportRequest,
  VehiclePlayback,
  VisualizationEventsValidationResult,
} from '../../types/simulationPlayback';
import type {
  AmhsLiveInventory,
  AmhsLiveReplicaBatch,
  AmhsLiveReportPayload,
  AmhsLiveWaitFrame,
  AmhsSimulationDiagnosticFrame,
  AmhsStockerDropSummary,
  AmhsStreamConnectionStatus,
  AmhsStreamDispatchPolicy,
  AmhsStreamJobStatus,
  AmhsStreamSnapshotFrame,
  OhtTimelineEvent,
} from '../../types/amhsStream';
import { buildReplayPlan, compileLegacyReplayPlan, type ReplayPlan } from '../../utils/replay';
import { alignEquipmentTransfersToStops } from '../../utils/simulationPlayback';
import { liveInventoryDisplayStore } from '../../store/liveInventoryDisplayStore';
import { liveStreamInventoryStore } from '../../store/liveStreamInventoryStore';
import { liveMotionStore } from '../../store/liveMotionStore';
import { transferWallHoldStore } from '../../store/transferWallHoldStore';
import { createLiveRateWindow, measureLiveSimRate, type LiveRateWindow } from './liveClock';
import type { ToolPickupReadyEntry } from './toolPickupReady';

/**
 * Wall-clock gap between Redux mirrors of live motion.
 *
 * Motion paints from `liveMotionStore`. Redux only needs a slow copy for
 * panels / 3D helpers that still select `browserKinetic` — rewriting those
 * fields every flush was the 10Hz React hitch.
 */
const REDUX_MOTION_MIRROR_MS = 1000;
const MAX_LIVE_DIAGNOSTICS = 100;
let lastReduxMotionMirrorMs = 0;

/**
 * Horizon granularity for the live replay plan, in simulation seconds.
 *
 * `ReplayPlan.durationS` is only ever read as an upper clamp on seek time, so
 * rounding it up to a coarse bucket keeps the clamp non-binding while stopping
 * the ever-growing live horizon from invalidating the plan on every flush.
 */
const LIVE_PLAN_HORIZON_BUCKET_S = 600;

/**
 * Upper bound on checkpoints in the live replay plan.
 *
 * Each checkpoint deep-clones the ownership ledger, and the ledger grows with
 * the run, so a fixed checkpoint stride made planning cost grow quadratically
 * with the event log. Capping the count keeps it linear; the only cost is
 * replaying more events forward from a checkpoint, which is O(1) per event and
 * only happens when the plan is re-seeked.
 */
const LIVE_PLAN_MAX_CHECKPOINTS = 64;

let liveReplayPlanCache: {
  events: AmhsLiveReplicaBatch['transferEvents'];
  durationS: number;
  plan: ReplayPlan;
} | null = null;

/**
 * Build the live replay plan, reusing the previous one while its inputs hold.
 *
 * The transfer-event log is never pruned, so re-planning it on every 10Hz
 * flush indexed, seeded and checkpointed a monotonically growing array — and
 * handed every `replayPlan` consumer a new identity each time. Most flushes
 * add no transfer events, so they can keep the plan they already have.
 */
function liveReplayPlan(
  events: AmhsLiveReplicaBatch['transferEvents'],
  horizonS: number
): ReplayPlan {
  const durationS =
    Math.ceil(Math.max(horizonS, 1) / LIVE_PLAN_HORIZON_BUCKET_S) *
    LIVE_PLAN_HORIZON_BUCKET_S;
  const cached = liveReplayPlanCache;
  if (cached && cached.events === events && cached.durationS === durationS) {
    return cached.plan;
  }
  const plan = buildReplayPlan(events, {
    version: 1,
    durationS,
    checkpointEvery: Math.max(32, Math.ceil(events.length / LIVE_PLAN_MAX_CHECKPOINTS)),
  });
  liveReplayPlanCache = { events, durationS, plan };
  return plan;
}

/** A single stop point on a track. */
export interface VehicleStop {
  id: string;
  /** Rail element ID this stop is on. */
  railId: string;
  /** Distance along the rail centerline (0 = start, railLength = end). */
  distance: number;
  /** Duration in seconds to stop, or null for indefinite. */
  durationS: number | null;
  /** Timestamp when last stopped here (for auto-resume). */
  stoppedTimestamp: number | null;
  /** Automatically update OHT loaded status to true/false when reaching this stop. */
  loaded?: boolean;
  /** Force the vehicle to take the alternative path at the next intersection. */
  switchBranch?: boolean;
  /** Optional name for the stop. */
  name?: string;
}

/** Per-vehicle control state for the simulation. */
export interface VehicleControl {
  /** Individually paused (independent of the global run state). */
  paused: boolean;
  /** Travel reversed relative to the vehicle's natural starting direction. */
  reversed: boolean;
  /** Node index the vehicle should stop at, or null for free running. */
  stopNodeId: number | null;
  /** Per-vehicle speed multiplier applied on top of the global speed. */
  speedFactor: number;
  /** Duration in seconds to stop at a node before auto-resuming, or null for indefinite. */
  stopDurationS: number | null;
  /** Timestamp when the vehicle last stopped at a node (for timed auto-resume). */
  stoppedAtNodeTimestamp: number | null;
  /** Array of rail element IDs to traverse in order (Specific Route). */
  route: string[];
  /** Current index into the route array. */
  routeIndex: number;
  /** Array of custom stop points on tracks. */
  stops: VehicleStop[];
}

export interface SimulationState {
  source: 'live' | 'legacy';
  /** Whether the animation engine is active. */
  running: boolean;
  /** Whether simulation time is currently advancing. */
  playing: boolean;
  /** Whether playback is paused. */
  paused: boolean;
  /** Whether the backend timeline reached its duration. */
  finished: boolean;
  /** Current backend simulation time, in seconds. */
  simulationTime: number;
  /** Multiplier applied to elapsed wall-clock time. */
  playbackSpeed: number;
  /** Total browser kinetic playback duration, in seconds. */
  durationS: number;
  /** Authoritative backend movement timeline. */
  browserKinetic: BrowserKinetic | null;
  /**
   * Indexed visualization replay plan when `visualizationEvents` validated.
   * Null for legacy responses or invalid timelines.
   */
  replayPlan: ReplayPlan | null;
  /** Validation issues for visualizationEvents (null when block omitted). */
  visualizationEventsValidation: VisualizationEventsValidationResult | null;
  /** Backend movement legs grouped by OHT. */
  playbackVehicles: Record<string, VehiclePlayback>;
  /** Requests displayed in the simulation request timeline. */
  transportRequests: TransportRequest[];
  /** Backend OHT assignments displayed in the request timeline. */
  assignments: SimulationAssignment[];
  /** Final report / compare payload for the Reports popup. */
  amhsReports: AmhsRunReports | null;
  /** Last live dispatch report retained after a stream stops. */
  lastLiveReport: AmhsLiveReportPayload | null;
  /** Global base speed in world units per second. */
  speed: number;
  /** Control state keyed by OHT element id. */
  vehicles: Record<string, VehicleControl>;
  live: {
    fabId: string | null;
    streamId: string | null;
    dispatchPolicy: AmhsStreamDispatchPolicy | null;
    connectionStatus: AmhsStreamConnectionStatus;
    jobStatus: AmhsStreamJobStatus | null;
    reconnected: boolean;
    simSpeed: number;
    serverTimeAnchorS: number;
    serverTimeAnchorWallMs: number;
    /** Delivered sim seconds per wall second — `simSpeed` is only a request. */
    rateWindow: LiveRateWindow;
    eventCursor: number | null;
    inventory: AmhsLiveInventory | null;
    kpis: Record<string, number>;
    collisions: Record<string, AmhsLiveWaitFrame>;
    ohtCargo: Record<string, string>;
    ohtTimelines: Record<string, OhtTimelineEvent[]>;
    lastStockerDrops: Record<string, AmhsStockerDropSummary>;
    /** Processed FOUPs waiting on tool docks, used for pickup-ready labels. */
    toolPickupReady: ToolPickupReadyEntry[];
    /** Stream diagnostics grouped by diagnosticId for the floating Errors panel. */
    diagnostics: AmhsSimulationDiagnosticFrame[];
    error: string | null;
    /** Rolling live dispatch KPI report from progress inventory frames. */
    liveReport: AmhsLiveReportPayload | null;
  };
}

export const DEFAULT_SIM_SPEED = 80;

const defaultVehicle = (): VehicleControl => ({
  paused: false,
  reversed: false,
  stopNodeId: null,
  speedFactor: 1,
  stopDurationS: null,
  stoppedAtNodeTimestamp: null,
  route: [],
  routeIndex: 0,
  stops: [],
});

const initialState: SimulationState = {
  source: 'legacy',
  running: false,
  playing: false,
  paused: false,
  finished: false,
  simulationTime: 0,
  playbackSpeed: 5,
  durationS: 0,
  browserKinetic: null,
  replayPlan: null,
  visualizationEventsValidation: null,
  playbackVehicles: {},
  transportRequests: [],
  assignments: [],
  amhsReports: null,
  lastLiveReport: null,
  speed: DEFAULT_SIM_SPEED,
  vehicles: {},
  live: {
    fabId: null,
    streamId: null,
    dispatchPolicy: null,
    connectionStatus: 'idle',
    jobStatus: null,
    reconnected: false,
    simSpeed: 5,
    serverTimeAnchorS: 0,
    serverTimeAnchorWallMs: 0,
    rateWindow: createLiveRateWindow(0, 0, 5),
    eventCursor: null,
    inventory: null,
    kpis: {},
    collisions: {},
    ohtCargo: {},
    ohtTimelines: {},
    lastStockerDrops: {},
    toolPickupReady: [],
    diagnostics: [],
    error: null,
    liveReport: null,
  },
};

const simulationSlice = createSlice({
  name: 'simulation',
  initialState,
  reducers: {
    startSimulation: (state) => {
      if (!state.browserKinetic) return;
      state.running = true;
      state.playing = true;
      state.paused = false;
      state.finished = false;
      if (state.simulationTime >= state.durationS) state.simulationTime = 0;
    },

    stopSimulation: (state) => {
      state.running = false;
      state.playing = false;
      state.paused = false;
      state.finished = false;
      state.simulationTime = 0;
      // Reset transient per-vehicle run flags but keep direction/speed prefs.
      Object.values(state.vehicles).forEach((v) => {
        v.paused = false;
        v.stopNodeId = null;
        v.stoppedAtNodeTimestamp = null;
      });
    },

    setSpeed: (state, action: PayloadAction<number>) => {
      state.speed = Math.max(1, action.payload);
    },

    loadSimulationResponse: (
      state,
      action: PayloadAction<{
        browserKinetic: BrowserKinetic;
        transportRequests: TransportRequest[];
        assignments: SimulationAssignment[];
        amhsReports?: AmhsRunReports | null;
      }>
    ) => {
      const { browserKinetic, transportRequests, assignments, amhsReports } = action.payload;
      state.source = 'legacy';
      state.browserKinetic = browserKinetic;
      state.visualizationEventsValidation =
        browserKinetic.visualizationEventsValidation ?? null;
      if (
        browserKinetic.visualizationEvents &&
        browserKinetic.visualizationEventsValidation?.ok
      ) {
        state.replayPlan = buildReplayPlan(
          alignEquipmentTransfersToStops(
            browserKinetic.visualizationEvents.events,
            browserKinetic
          ),
          {
            version: browserKinetic.visualizationEvents.version,
            durationS: browserKinetic.durationS,
          }
        );
      } else {
        // Legacy responses are compiled once at the boundary. Every visual
        // consumer still reads one deterministic, request-scoped replay plan;
        // none may fall back to nearest-object or final-state animation.
        state.replayPlan = compileLegacyReplayPlan(browserKinetic, transportRequests);
      }
      state.playbackVehicles = Object.fromEntries(
        Array.from(
          new Set([
            ...Object.keys(browserKinetic.vehicleLegs.idleByVehicle),
            ...Object.keys(browserKinetic.vehicleLegs.legsByVehicle),
          ])
        ).map((ohtId) => {
          const legs = browserKinetic.vehicleLegs.legsByVehicle[ohtId] ?? [];
          return [
            ohtId,
            {
              ohtId,
              legs: [...legs].sort((a, b) => a.startS - b.startS || a.endS - b.endS),
            },
          ];
        })
      );
      state.transportRequests = transportRequests;
      state.assignments = assignments;
      state.amhsReports = amhsReports ?? null;
      state.durationS = browserKinetic.durationS;
      state.simulationTime = 0;
      state.running = false;
      state.playing = false;
      state.paused = false;
      state.finished = false;
    },

    setLiveConnectionStatus: (
      state,
      action: PayloadAction<{
        status: AmhsStreamConnectionStatus;
        fabId?: string;
        error?: string | null;
      }>
    ) => {
      if (action.payload.status !== 'idle' && action.payload.status !== 'discovering') {
        state.source = 'live';
      }
      state.live.connectionStatus = action.payload.status;
      if (action.payload.fabId) state.live.fabId = action.payload.fabId;
      if (action.payload.error !== undefined) state.live.error = action.payload.error;
    },

    liveStreamStarted: (
      state,
      action: PayloadAction<{
        fabId: string;
        streamId: string;
        simTimeS: number;
        simSpeed: number;
        reconnected: boolean;
        receivedAtMs: number;
        dispatchPolicy?: AmhsStreamDispatchPolicy;
      }>
    ) => {
      state.source = 'live';
      state.running = true;
      state.playing = true;
      state.paused = false;
      state.finished = false;
      state.live.fabId = action.payload.fabId;
      state.live.streamId = action.payload.streamId;
      state.live.dispatchPolicy = action.payload.dispatchPolicy ?? state.live.dispatchPolicy;
      state.live.connectionStatus = 'running';
      state.live.jobStatus = 'running';
      state.live.reconnected = action.payload.reconnected;
      state.live.simSpeed = action.payload.simSpeed;
      state.live.serverTimeAnchorS = action.payload.simTimeS;
      state.live.serverTimeAnchorWallMs = action.payload.receivedAtMs;
      state.live.rateWindow = createLiveRateWindow(
        action.payload.simTimeS,
        action.payload.receivedAtMs,
        action.payload.simSpeed
      );
      state.live.error = null;
      if (!action.payload.reconnected) {
        state.live.liveReport = null;
        state.lastLiveReport = null;
        state.live.diagnostics = state.live.diagnostics.filter(
          (diagnostic) => diagnostic.phase === 'preflight'
        );
      }
      state.simulationTime = action.payload.simTimeS;
      state.durationS = Math.max(state.durationS, action.payload.simTimeS + 60);
      state.amhsReports = null;
    },

    applyLiveSnapshot: (
      state,
      action: PayloadAction<{ frame: AmhsStreamSnapshotFrame; receivedAtMs: number }>
    ) => {
      const { frame, receivedAtMs } = action.payload;
      state.source = 'live';
      state.running = true;
      state.playing = frame.status === 'running';
      state.finished =
        frame.status === 'completed' || frame.status === 'cancelled' || frame.status === 'failed';
      state.live.streamId = frame.streamId;
      state.live.fabId = frame.fabId;
      state.live.jobStatus = frame.status;
      state.live.simSpeed = frame.simSpeed;
      state.live.serverTimeAnchorS = frame.simTimeS;
      state.live.serverTimeAnchorWallMs = receivedAtMs;
      // A snapshot is authoritative, so restart the rate estimate with it.
      state.live.rateWindow = createLiveRateWindow(
        frame.simTimeS,
        receivedAtMs,
        frame.simSpeed
      );
      state.live.eventCursor = frame.eventCursor;
      state.live.inventory = frame.snapshot.inventory ?? null;
      state.live.kpis = frame.snapshot.kpis ?? {};
      state.simulationTime = frame.simTimeS;
      state.durationS = Math.max(frame.simTimeS + 60, state.durationS);
    },

    applyLiveReplicaBatch: (
      state,
      action: PayloadAction<{
        batch: AmhsLiveReplicaBatch;
        receivedAtMs: number;
      }>
    ) => {
      const { batch, receivedAtMs } = action.payload;
      // Motion paints from this store. Redux no longer owns the per-flush leg
      // graph — that was the 10Hz React hitch on complex layouts.
      const { fleetChanged } = liveMotionStore.applyBatch(batch);
      const motion = liveMotionStore.getSnapshot();

      let durationS = Math.max(batch.simTimeS + 60, 60);
      for (const leg of batch.legs) {
        if (leg.endS > durationS) durationS = leg.endS;
      }

      const shouldMirrorMotion =
        fleetChanged ||
        !state.browserKinetic ||
        receivedAtMs - lastReduxMotionMirrorMs >= REDUX_MOTION_MIRROR_MS;
      if (shouldMirrorMotion) {
        lastReduxMotionMirrorMs = receivedAtMs;
        state.browserKinetic = {
          policy: 'live',
          fabId: state.live.fabId ?? '',
          durationS,
          kpis: batch.kpis ?? state.live.kpis,
          vehicleLegs: {
            idleByVehicle: motion.idleByVehicle,
            legsByVehicle: motion.legsByVehicle,
          },
          events: [],
          keyframes: [],
          assignmentPlanRef: state.live.streamId ?? 'live',
          apiLegs: motion.apiLegs,
          visualizationEvents: { version: 1, events: batch.transferEvents },
        };
        state.playbackVehicles = motion.playbackVehicles;
        state.live.ohtTimelines = motion.ohtTimelines;
      }
      // When not mirroring, leave browserKinetic / playbackVehicles untouched so
      // React subscribers keep the same references between fleet changes.

      state.replayPlan = liveReplayPlan(batch.transferEvents, durationS);
      state.transportRequests = batch.transportRequests;
      state.durationS = durationS;
      state.live.serverTimeAnchorS = batch.simTimeS;
      state.live.serverTimeAnchorWallMs = receivedAtMs;
      state.live.rateWindow = measureLiveSimRate(
        state.live.rateWindow,
        batch.simTimeS,
        receivedAtMs,
        state.live.simSpeed
      );
      state.live.inventory = batch.inventory ?? state.live.inventory;
      state.live.kpis = batch.kpis ?? state.live.kpis;
      state.live.collisions = batch.collisions;
      state.live.ohtCargo = batch.ohtCargo;
      state.live.lastStockerDrops = batch.lastStockerDrops;
      state.live.toolPickupReady = batch.toolPickupReady;
      state.live.eventCursor = batch.eventCursor ?? state.live.eventCursor;
      if (batch.liveReport) state.live.liveReport = batch.liveReport;
    },

    receiveLiveReport: (state, action: PayloadAction<AmhsLiveReportPayload | null>) => {
      if (!action.payload) return;
      state.live.liveReport = action.payload;
    },

    receiveSimulationDiagnostic: (
      state,
      action: PayloadAction<AmhsSimulationDiagnosticFrame>
    ) => {
      if (action.payload.severity !== 'error') return;
      const index = state.live.diagnostics.findIndex(
        (diagnostic) => diagnostic.diagnosticId === action.payload.diagnosticId
      );
      if (index >= 0) {
        state.live.diagnostics[index] = action.payload;
      } else {
        if (state.live.diagnostics.length >= MAX_LIVE_DIAGNOSTICS) {
          state.live.diagnostics.shift();
        }
        state.live.diagnostics.push(action.payload);
      }
    },

    applyPreflightDiagnostics: (
      state,
      action: PayloadAction<AmhsSimulationDiagnosticFrame[]>
    ) => {
      state.live.diagnostics = [
        ...action.payload,
        ...state.live.diagnostics.filter((diagnostic) => diagnostic.phase !== 'preflight'),
      ];
    },

    anchorLiveClock: (
      state,
      action: PayloadAction<{
        simTimeS: number;
        receivedAtMs: number;
        jobStatus?: AmhsStreamJobStatus;
      }>
    ) => {
      state.live.serverTimeAnchorS = action.payload.simTimeS;
      state.live.serverTimeAnchorWallMs = action.payload.receivedAtMs;
      state.live.rateWindow = measureLiveSimRate(
        state.live.rateWindow,
        action.payload.simTimeS,
        action.payload.receivedAtMs,
        state.live.simSpeed
      );
      state.simulationTime = action.payload.simTimeS;
      state.durationS = Math.max(state.durationS, action.payload.simTimeS + 60);
      if (action.payload.jobStatus) state.live.jobStatus = action.payload.jobStatus;
    },

    liveStreamTerminal: (
      state,
      action: PayloadAction<{
        status?: AmhsStreamJobStatus;
        error?: string | null;
      }>
    ) => {
      state.running = false;
      state.playing = false;
      state.paused = false;
      state.finished = true;
      state.live.connectionStatus = action.payload.error ? 'failed' : 'closed';
      state.live.jobStatus = action.payload.status ?? state.live.jobStatus;
      state.live.error = action.payload.error ?? null;
    },

    resetLiveStream: (state) => {
      liveReplayPlanCache = null;
      lastReduxMotionMirrorMs = 0;
      liveMotionStore.reset();
      liveInventoryDisplayStore.reset();
      liveStreamInventoryStore.reset();
      transferWallHoldStore.reset();
      const wasLive = state.source === 'live';
      if (state.live.liveReport) {
        state.lastLiveReport = state.live.liveReport;
      }
      state.live = {
        ...initialState.live,
        kpis: {},
        collisions: {},
        ohtCargo: {},
        ohtTimelines: {},
        lastStockerDrops: {},
        toolPickupReady: [],
        diagnostics: [],
        liveReport: null,
      };
      if (!wasLive) return;
      state.source = 'legacy';
      state.running = false;
      state.playing = false;
      state.paused = false;
      state.finished = false;
      state.browserKinetic = null;
      state.replayPlan = null;
      state.playbackVehicles = {};
      state.transportRequests = [];
      state.simulationTime = 0;
      state.durationS = 0;
    },

    advanceSimulationTime: (state, action: PayloadAction<number>) => {
      if (!state.running || !state.playing || state.paused || state.finished) return;
      state.simulationTime = Math.min(
        state.durationS,
        state.simulationTime + Math.max(0, action.payload) * state.playbackSpeed
      );
      if (state.simulationTime >= state.durationS) {
        state.playing = false;
        state.finished = true;
      }
    },

    /**
     * Push high-frequency clock time into Redux at a throttled rate for UI.
     * Does not own playback advancement — that lives in simulationClockStore.
     */
    syncUiSimulationTime: (state, action: PayloadAction<number>) => {
      if (!state.running) return;
      if (state.source === 'live') {
        state.simulationTime = Math.max(0, action.payload);
        state.durationS = Math.max(state.durationS, state.simulationTime + 60);
        return;
      }
      state.simulationTime = Math.min(state.durationS, Math.max(0, action.payload));
      if (state.simulationTime >= state.durationS) {
        state.playing = false;
        state.finished = true;
      }
    },

    pausePlayback: (state) => {
      if (!state.running || state.finished) return;
      state.playing = false;
      state.paused = true;
    },

    resumePlayback: (state) => {
      if (!state.running || state.finished) return;
      state.playing = true;
      state.paused = false;
    },

    restartSimulation: (state) => {
      if (!state.browserKinetic) return;
      state.simulationTime = 0;
      state.running = true;
      state.playing = true;
      state.paused = false;
      state.finished = false;
    },

    /**
     * Jump simulation time to an absolute second. Keeps playback running unless
     * the seek lands at/after the end (then marks finished).
     */
    seekSimulationTime: (state, action: PayloadAction<number>) => {
      if (!state.browserKinetic || state.durationS <= 0) return;
      const next = Math.max(0, Math.min(state.durationS, action.payload));
      state.simulationTime = next;
      state.running = true;
      if (next >= state.durationS) {
        state.playing = false;
        state.paused = false;
        state.finished = true;
        return;
      }
      state.finished = false;
      // Keep paused state if the user was paused; otherwise keep playing.
      if (!state.paused) {
        state.playing = true;
      }
    },

    /** Nudge simulation time forward/backward by a relative delta (seconds). */
    nudgeSimulationTime: (state, action: PayloadAction<number>) => {
      if (!state.browserKinetic || state.durationS <= 0) return;
      const next = Math.max(0, Math.min(state.durationS, state.simulationTime + action.payload));
      state.simulationTime = next;
      state.running = true;
      if (next >= state.durationS) {
        state.playing = false;
        state.paused = false;
        state.finished = true;
        return;
      }
      state.finished = false;
      if (!state.paused) {
        state.playing = true;
      }
    },

    setPlaybackSpeed: (state, action: PayloadAction<number>) => {
      const supportedSpeeds = [0.5, 1, 2, 5, 10, 15, 20];
      if (supportedSpeeds.includes(action.payload)) {
        state.playbackSpeed = action.payload;
      }
    },

    ensureVehicles: (state, action: PayloadAction<string[]>) => {
      const ids = new Set(action.payload);
      action.payload.forEach((id) => {
        if (!state.vehicles[id]) state.vehicles[id] = defaultVehicle();
      });
      // Drop control entries for vehicles that no longer exist.
      Object.keys(state.vehicles).forEach((id) => {
        if (!ids.has(id)) delete state.vehicles[id];
      });
    },

    setVehiclePaused: (state, action: PayloadAction<{ id: string; paused: boolean }>) => {
      const v = state.vehicles[action.payload.id];
      if (v) {
        v.paused = action.payload.paused;
        // Clear stop timestamp when manually resuming
        if (!action.payload.paused) {
          v.stoppedAtNodeTimestamp = null;
        }
      }
    },

    toggleVehiclePaused: (state, action: PayloadAction<string>) => {
      const v = state.vehicles[action.payload];
      if (v) v.paused = !v.paused;
    },

    setVehicleReversed: (state, action: PayloadAction<{ id: string; reversed: boolean }>) => {
      const v = state.vehicles[action.payload.id];
      if (v) v.reversed = action.payload.reversed;
    },

    toggleVehicleReversed: (state, action: PayloadAction<string>) => {
      const v = state.vehicles[action.payload];
      if (v) v.reversed = !v.reversed;
    },

    setVehicleStopNode: (
      state,
      action: PayloadAction<{ id: string; stopNodeId: number | null }>
    ) => {
      const v = state.vehicles[action.payload.id];
      if (v) v.stopNodeId = action.payload.stopNodeId;
    },

    setVehicleStopDuration: (
      state,
      action: PayloadAction<{ id: string; stopDurationS: number | null }>
    ) => {
      const v = state.vehicles[action.payload.id];
      if (v) v.stopDurationS = action.payload.stopDurationS;
    },

    setVehicleStoppedTimestamp: (
      state,
      action: PayloadAction<{ id: string; timestamp: number | null }>
    ) => {
      const v = state.vehicles[action.payload.id];
      if (v) v.stoppedAtNodeTimestamp = action.payload.timestamp;
    },

    setVehicleSpeedFactor: (state, action: PayloadAction<{ id: string; speedFactor: number }>) => {
      const v = state.vehicles[action.payload.id];
      if (v) v.speedFactor = Math.max(0.1, action.payload.speedFactor);
    },

    addVehicleStop: (
      state,
      action: PayloadAction<{
        vehicleId: string;
        stop: Omit<VehicleStop, 'id' | 'stoppedTimestamp'>;
      }>
    ) => {
      const v = state.vehicles[action.payload.vehicleId];
      if (v) {
        const newStop: VehicleStop = {
          ...action.payload.stop,
          id: `stop-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          stoppedTimestamp: null,
        };
        v.stops.push(newStop);
      }
    },

    removeVehicleStop: (state, action: PayloadAction<{ vehicleId: string; stopId: string }>) => {
      const v = state.vehicles[action.payload.vehicleId];
      if (v) {
        v.stops = v.stops.filter((s) => s.id !== action.payload.stopId);
      }
    },

    updateVehicleStop: (
      state,
      action: PayloadAction<{
        vehicleId: string;
        stopId: string;
        updates: Partial<Omit<VehicleStop, 'id'>>;
      }>
    ) => {
      const v = state.vehicles[action.payload.vehicleId];
      if (v) {
        const stop = v.stops.find((s) => s.id === action.payload.stopId);
        if (stop) {
          Object.assign(stop, action.payload.updates);
        }
      }
    },

    clearVehicleStopTimestamp: (
      state,
      action: PayloadAction<{ vehicleId: string; stopId: string }>
    ) => {
      const v = state.vehicles[action.payload.vehicleId];
      if (v) {
        const stop = v.stops.find((s) => s.id === action.payload.stopId);
        if (stop) {
          stop.stoppedTimestamp = null;
        }
      }
    },

    setVehicleRoute: (state, action: PayloadAction<{ vehicleId: string; route: string[] }>) => {
      const v = state.vehicles[action.payload.vehicleId];
      if (v) {
        v.route = action.payload.route;
        v.routeIndex = 0;
      }
    },

    setVehicleRouteIndex: (state, action: PayloadAction<{ vehicleId: string; index: number }>) => {
      const v = state.vehicles[action.payload.vehicleId];
      if (v) {
        v.routeIndex = action.payload.index;
      }
    },

    pauseAll: (state) => {
      state.playing = false;
      state.paused = true;
      Object.values(state.vehicles).forEach((v) => {
        v.paused = true;
      });
    },

    resumeAll: (state) => {
      if (state.running && !state.finished) {
        state.playing = true;
        state.paused = false;
      }
      Object.values(state.vehicles).forEach((v) => {
        v.paused = false;
        v.stoppedAtNodeTimestamp = null;
      });
    },

    loadSimulationState: (
      state,
      action: PayloadAction<{
        speed: number;
        vehicles: Record<
          string,
          {
            reversed: boolean;
            stopNodeId: number | null;
            speedFactor: number;
            stopDurationS: number | null;
            route?: string[];
            routeIndex?: number;
            stops?: Array<Omit<VehicleStop, 'stoppedTimestamp'>>;
          }
        >;
      }>
    ) => {
      state.speed = action.payload.speed;
      // Restore vehicle controls, adding default values for runtime-only fields
      state.vehicles = {};
      Object.entries(action.payload.vehicles).forEach(([id, v]) => {
        state.vehicles[id] = {
          ...v,
          paused: false,
          stoppedAtNodeTimestamp: null,
          route: v.route || [],
          routeIndex: v.routeIndex ?? 0,
          stops: (v.stops || []).map((s) => ({ ...s, stoppedTimestamp: null })),
        };
      });
    },
  },
});

export const {
  startSimulation,
  stopSimulation,
  setSpeed,
  loadSimulationResponse,
  setLiveConnectionStatus,
  liveStreamStarted,
  applyLiveSnapshot,
  applyLiveReplicaBatch,
  receiveLiveReport,
  applyPreflightDiagnostics,
  receiveSimulationDiagnostic,
  anchorLiveClock,
  liveStreamTerminal,
  resetLiveStream,
  advanceSimulationTime,
  syncUiSimulationTime,
  pausePlayback,
  resumePlayback,
  restartSimulation,
  seekSimulationTime,
  nudgeSimulationTime,
  setPlaybackSpeed,
  ensureVehicles,
  setVehiclePaused,
  toggleVehiclePaused,
  setVehicleReversed,
  toggleVehicleReversed,
  setVehicleStopNode,
  setVehicleSpeedFactor,
  setVehicleStopDuration,
  setVehicleStoppedTimestamp,
  addVehicleStop,
  removeVehicleStop,
  updateVehicleStop,
  clearVehicleStopTimestamp,
  setVehicleRoute,
  setVehicleRouteIndex,
  pauseAll,
  resumeAll,
  loadSimulationState,
} = simulationSlice.actions;

export default simulationSlice.reducer;
